// Extended character metadata — age group, values, traits, and longer
// bios used by the public character detail page and any surface that
// wants to introduce a character beyond just their portrait.
//
// This is the source of truth for "who each Jozori character is" in
// human, parent-facing language. The persona registry in
// character-personality.ts owns the in-app voice lines.

import type { CharacterCode } from "./character-personality";

export type AgeGroup = "4-6" | "7-9" | "10-12" | "all-ages";

export type CharacterMeta = {
  code: CharacterCode;
  ageGroup: AgeGroup;
  ageLabelEn: string;
  ageLabelAr: string;
  tierEn: string;
  tierAr: string;
  bioEn: string;
  bioAr: string;
  traitsEn: string[];
  traitsAr: string[];
  valuesEn: string[];
  valuesAr: string[];
};

export const CHARACTER_META: Record<CharacterCode, CharacterMeta> = {
  jad: {
    code: "jad",
    ageGroup: "4-6",
    ageLabelEn: "Ages 4–6",
    ageLabelAr: "٤–٦ سنوات",
    tierEn: "Buds",
    tierAr: "براعم",
    bioEn:
      "Jad is the youngest explorer of the Jozori friends. He wakes up curious every morning, asks a hundred 'why?' questions, and turns every walk into a tiny adventure. He helps little learners feel safe trying new sounds, new words, and new ideas.",
    bioAr:
      "جاد أصغر مستكشف بين أصدقاء جذوري. يصحو فضوليًا كل صباح، يسأل مئة سؤال «ليش؟»، ويحوّل أي مشوار صغير إلى مغامرة. يساعد الأطفال الصغار على الشعور بالأمان وهم يجرّبون أصواتًا وكلمات وأفكارًا جديدة.",
    traitsEn: ["Curious", "Brave", "Playful", "Open-hearted"],
    traitsAr: ["فضولي", "شجاع", "مرح", "قلب مفتوح"],
    valuesEn: ["Curiosity", "Courage to try", "Wonder"],
    valuesAr: ["الفضول", "شجاعة التجربة", "الدهشة"],
  },
  julia: {
    code: "julia",
    ageGroup: "4-6",
    ageLabelEn: "Ages 4–6",
    ageLabelAr: "٤–٦ سنوات",
    tierEn: "Buds",
    tierAr: "براعم",
    bioEn:
      "Julia colors the world with kindness. She sings, draws, and notices the small feelings other people miss. With Julia, children learn that a soft voice and a creative hand can make any day brighter.",
    bioAr:
      "جوليا تلوّن العالم باللطف. تغنّي وترسم وتنتبه إلى المشاعر الصغيرة التي يفوتها الآخرون. مع جوليا يتعلّم الأطفال أن الصوت الهادئ واليد المبدعة قادرَين على إضاءة أي يوم.",
    traitsEn: ["Kind", "Creative", "Gentle", "Thoughtful"],
    traitsAr: ["لطيفة", "مبدعة", "هادئة", "مراعية"],
    valuesEn: ["Kindness", "Creativity", "Empathy"],
    valuesAr: ["اللطف", "الإبداع", "التعاطف"],
  },
  anas: {
    code: "anas",
    ageGroup: "7-9",
    ageLabelEn: "Ages 7–9",
    ageLabelAr: "٧–٩ سنوات",
    tierEn: "Branches",
    tierAr: "أغصان",
    bioEn:
      "Anas is the thinker of the group. He loves puzzles, tricky questions, and the moment right before an idea clicks. He shows growing minds that slowing down to think is its own kind of superpower.",
    bioAr:
      "أنس مفكّر المجموعة. يحبّ الألغاز والأسئلة الصعبة، ويعشق اللحظة التي تومض فيها الفكرة. يعلّم العقول الناشئة أن التروّي في التفكير قوّة بحدّ ذاتها.",
    traitsEn: ["Thoughtful", "Patient", "Resourceful", "Honest"],
    traitsAr: ["متأمّل", "صبور", "واسع الحيلة", "صادق"],
    valuesEn: ["Critical thinking", "Patience", "Honesty"],
    valuesAr: ["التفكير الناقد", "الصبر", "الصدق"],
  },
  talia: {
    code: "talia",
    ageGroup: "7-9",
    ageLabelEn: "Ages 7–9",
    ageLabelAr: "٧–٩ سنوات",
    tierEn: "Branches",
    tierAr: "أغصان",
    bioEn:
      "Talia leads with heart. She speaks up when something isn't fair, hugs the friend who is left out, and reminds everyone that real confidence is built on care. She is the friend you wish your child had.",
    bioAr:
      "تاليا تقود بالقلب. ترفع صوتها إذا حدث ما هو غير عادل، تحتضن الصديق المنسي، وتُذكّر الجميع بأن الثقة الحقيقية تُبنى على الاهتمام. هي الصديقة التي تتمنّى أن يعرفها طفلك.",
    traitsEn: ["Confident", "Empathetic", "Loyal", "Brave"],
    traitsAr: ["واثقة", "متعاطفة", "وفيّة", "شجاعة"],
    valuesEn: ["Confidence", "Empathy", "Standing up for others"],
    valuesAr: ["الثقة", "التعاطف", "نصرة الآخرين"],
  },
  yousef: {
    code: "yousef",
    ageGroup: "10-12",
    ageLabelEn: "Ages 10–12",
    ageLabelAr: "١٠–١٢ سنة",
    tierEn: "Fruits",
    tierAr: "ثمار",
    bioEn:
      "Yousef is the quiet older friend. Calm, thoughtful, and self-aware, he shows children that real strength is gentle — that it's okay to pause, breathe, and think before you act. With Yousef, courage becomes a quiet daily habit.",
    bioAr:
      "يوسف هو الصديق الأكبر الهادئ. متأمّل، واعٍ بنفسه، يُري الأطفال أن القوة الحقيقية لطيفة — وأنّه لا بأس بأن تتمهّل وتتنفّس وتفكّر قبل أن تتصرّف. مع يوسف، تصير الشجاعة عادة يومية هادئة.",
    traitsEn: ["Calm", "Thoughtful", "Self-aware", "Steady"],
    traitsAr: ["هادئ", "متأمّل", "واعٍ بنفسه", "ثابت"],
    valuesEn: ["Self-awareness", "Quiet courage", "Responsibility"],
    valuesAr: ["وعي الذات", "الشجاعة الهادئة", "المسؤولية"],
  },
  luna: {
    code: "luna",
    ageGroup: "10-12",
    ageLabelEn: "Ages 10–12",
    ageLabelAr: "١٠–١٢ سنة",
    tierEn: "Fruits",
    tierAr: "ثمار",
    bioEn:
      "Luna is the reflective heart of the older friends. Warm, emotionally intelligent, and gently honest, she helps children name what they feel and listen to themselves before reacting. With Luna, growing up feels safe and seen.",
    bioAr:
      "لونا هي القلب المتأمّل بين الأصدقاء الأكبر. دافئة، ذكية المشاعر، صادقة بلطف، تساعد الأطفال على تسمية ما يشعرون به والإصغاء لأنفسهم قبل أن يتصرّفوا. مع لونا، تصير مرحلة الكبر مساحة آمنة ومرئية.",
    traitsEn: ["Warm", "Reflective", "Emotionally wise", "Honest"],
    traitsAr: ["دافئة", "متأمّلة", "حكيمة المشاعر", "صادقة"],
    valuesEn: ["Empathy", "Self-reflection", "Honest expression"],
    valuesAr: ["التعاطف", "التأمّل الذاتي", "التعبير الصادق"],
  },
};

export function getCharacterMeta(code: string | null | undefined): CharacterMeta | null {
  if (!code) return null;
  return (CHARACTER_META as Record<string, CharacterMeta>)[code] ?? null;
}

// Arabic translations for free-form trait labels that come from the database
// (where traits are stored as lowercase English strings). Used to render the
// trait pills in Arabic on the public character pages.
const TRAIT_AR: Record<string, string> = {
  curious: "فضولي",
  kind: "لطيف",
  imaginative: "خيالي",
  brave: "شجاع",
  leader: "قائد",
  honest: "صادق",
  thoughtful: "متأمّل",
  fair: "منصف",
  clever: "ذكي",
  gentle: "هادئ",
  expressive: "معبّر",
  artistic: "فنّان",
  patient: "صبور",
  wise: "حكيم",
  responsible: "مسؤول",
  calm: "هادئ",
  protective: "حامي",
  playful: "مرح",
  loyal: "وفيّ",
  empathetic: "متعاطف",
  confident: "واثق",
  observant: "ملاحظ",
  visionary: "صاحب رؤية",
  steady: "ثابت",
  courageous: "شجاع",
  respectful: "محترِم",
  resourceful: "واسع الحيلة",
  creative: "مبدع",
  "open-hearted": "قلب مفتوح",
};

export function translateTrait(trait: string, isAr: boolean): string {
  if (!isAr) return trait;
  const key = trait.trim().toLowerCase();
  return TRAIT_AR[key] ?? trait;
}

